import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { getSocket } from '../api/socket';
import { useAuth } from '../context/AuthContext';
import { useRoundTimer } from '../hooks/useRoundTimer';
import ResultsByBidder from './ResultsByBidder';

interface Props {
  auction: any;
  onBack: () => void;
}

function getAuctionRoundStatus(auction: any) {
  if (auction.status === 'CLOSED') return { text: 'Auction Closed', bg: '#fce8e6', color: '#c62828' };
  if (auction.status === 'CLOCK_COMPLETE') return { text: 'Clock Phase Complete', bg: '#fff8e1', color: '#f57f17' };
  const active = auction.rounds?.find((r: any) => r.status === 'ACTIVE');
  if (active) return { text: `Round ${active.roundNumber} Open`, bg: '#e6f4ea', color: '#1e7e34' };
  const lastClosed = [...(auction.rounds ?? [])].reverse().find((r: any) => r.status === 'CLOSED');
  if (lastClosed) return { text: `Round ${lastClosed.roundNumber} Closed`, bg: '#fff3e0', color: '#e65100' };
  return { text: 'Auction Pending', bg: '#e8eaf6', color: '#3949ab' };
}

// Displayed prices carry thousands separators; a missing price renders as a dash.
// Display only -- CSV exports and number-input bounds must stay unformatted.
const fmtPrice = (v: any) => (typeof v === 'number' ? v.toLocaleString() : '—');

export default function BidderAuctionView({ auction, onBack }: Props) {
  const { user } = useAuth();
  const [detail, setDetail] = useState<any>(auction);
  const [currentRound, setCurrentRound] = useState<any | null>(null);
  const [eligibility, setEligibility] = useState<number | null>(null);
  // Annex II 3.7.1 allowance in eligibility points; 0 in the auction's first round.
  const [activityTolerance, setActivityTolerance] = useState<number>(0);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [myBid, setMyBid] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [lastDemand, setLastDemand] = useState<any[] | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [myLastRoundBid, setMyLastRoundBid] = useState<Record<string, number>>({});
  const [myAllRoundBids, setMyAllRoundBids] = useState<Record<string, Record<string, number>>>({});
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [roundEndTime, setRoundEndTime] = useState<string | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const [showIntegration, setShowIntegration] = useState(false);
  const [copied, setCopied] = useState<string>('');
  const [showHistory, setShowHistory] = useState(false);
  const [showRoundResults, setShowRoundResults] = useState(true);
  const [activeExitBids, setActiveExitBids] = useState<any[]>([]);
  const [retainedExitBidIds, setRetainedExitBidIds] = useState<Set<string>>(new Set());
  const [finalResults, setFinalResults] = useState<any | null>(null);
  // {categoryId: {additionalQty: price}} — one entry per "+N" level; missing or 0 price = not submitted
  const [newExitBidInputs, setNewExitBidInputs] = useState<Record<string, Record<number, number>>>({});
  const { label: timerLabel, timeLeft } = useRoundTimer(roundEndTime);

  const loadDetail = useCallback(async () => {
    const { data } = await api.get(`/auctions/${auction.id}`);
    setDetail(data);
    const active = data.rounds.find((r: any) => r.status === 'ACTIVE');
    setCurrentRound(active ?? null);
    setRoundEndTime(active?.endTime ?? null);
    let hasBidThisRound = false;
    if (active) {
      // Load my existing bid if any
      try {
        const { data: bid } = await api.get(`/rounds/${active.id}/bids/my`);
        if (bid) {
          setMyBid(bid);
          const qMap: Record<string, number> = {};
          for (const item of bid.items) qMap[item.categoryId] = item.quantity;
          setQuantities(qMap);
          setSubmitted(true);
          hasBidThisRound = true;
        } else {
          setSubmitted(false);
        }
      } catch {
        setSubmitted(false);
      }
      // Load my eligibility. Use openingEligibility to match what the server enforces
      // (see routes/bids.py) -- the two are equal for an active round, but `eligibility`
      // is the field close_round rewrites.
      const eligRound = active.eligibilities?.find((e: any) => e.userId === user?.id);
      if (eligRound) setEligibility(eligRound.openingEligibility ?? eligRound.eligibility);
      setActivityTolerance(active.activityTolerance ?? 0);
      // Load active exit bids
      try {
        const { data: ebs } = await api.get(`/rounds/${active.id}/bids/exit-bids/my`);
        setActiveExitBids(ebs ?? []);
        // Default: retain all active exit bids
        setRetainedExitBidIds(new Set((ebs ?? []).map((eb: any) => eb.id)));
      } catch {
        setActiveExitBids([]);
      }
    } else {
      setActiveExitBids([]);
      setRetainedExitBidIds(new Set());
    }
    // Load my bids for all closed rounds in parallel
    const closedRounds = data.rounds.filter((r: any) => r.status === 'CLOSED');
    if (closedRounds.length > 0) {
      const results = await Promise.all(
        closedRounds.map((r: any) =>
          api.get(`/rounds/${r.id}/bids/my`)
            .then(res => {
              const qMap: Record<string, number> = {};
              if (res.data) for (const item of res.data.items) qMap[item.categoryId] = item.quantity;
              return [r.id, qMap] as [string, Record<string, number>];
            })
            .catch(() => [r.id, {}] as [string, Record<string, number>])
        )
      );
      const allBids = Object.fromEntries(results);
      setMyAllRoundBids(allBids);
      // keep myLastRoundBid in sync for the round results table
      const lastClosed = [...closedRounds].sort((a: any, b: any) => b.roundNumber - a.roundNumber)[0];
      const lastBid = allBids[lastClosed.id] ?? {};
      setMyLastRoundBid(lastBid);
      // Open the form on last round's quantities rather than zeros: standing demand is the
      // sensible starting point, and a bidder reducing edits down from it. Only when nothing
      // has been submitted this round -- an existing bid must never be overwritten. Seeding
      // from the same value exitBidEligibility treats as the previous quantity also means the
      // default shows no reduction, so no exit-bid rungs appear until the bidder reduces.
      if (active && !hasBidThisRound) setQuantities(lastBid);
    } else {
      setMyAllRoundBids({});
      setMyLastRoundBid({});
    }
    // Load final results if auction is closed
    if (data.status === 'CLOSED') {
      try {
        const { data: res } = await api.get(`/auctions/${auction.id}/results`);
        setFinalResults(res);
      } catch {
        setFinalResults(null);
      }
    } else {
      setFinalResults(null);
    }
  }, [auction.id, user?.id]);

  useEffect(() => {
    loadDetail();
    const socket = getSocket();
    if (!socket) return;
    socket.emit('join:auction', auction.id);

    socket.on('round:opened', (data: any) => {
      setRoundEndTime(data.endTime ?? null);
      loadDetail();
      setSubmitted(false);
      setMyBid(null);
      setError('');
      setNewExitBidInputs({});
    });

    socket.on('round:closed', (data: any) => {
      setRoundEndTime(null);
      setLastDemand(data.aggregateDemand);
      setCurrentRound(null);
      loadDetail();
    });

    socket.on('eligibility:updated', (data: any) => {
      setEligibility(data.eligibility);
      if (data.activityTolerance !== undefined) setActivityTolerance(data.activityTolerance);
    });

    socket.on('auction:clock-complete', () => {
      loadDetail();
    });

    socket.on('auction:closed', () => {
      loadDetail();
    });

    return () => {
      socket.emit('leave:auction', auction.id);
      socket.off('round:opened');
      socket.off('round:closed');
      socket.off('eligibility:updated');
      socket.off('auction:clock-complete');
      socket.off('auction:closed');
    };
  }, [auction.id, loadDetail]);

  // Auto-cancel retained exit bids when clock demand increases above previous round for that category
  useEffect(() => {
    if (!currentRound || currentRound.roundNumber <= 1 || activeExitBids.length === 0) return;
    setRetainedExitBidIds(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const eb of activeExitBids) {
        const prevQty = myLastRoundBid[eb.categoryId] ?? 0;
        const currQty = quantities[eb.categoryId] ?? 0;
        if (currQty > prevQty && next.has(eb.id)) {
          next.delete(eb.id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [quantities, activeExitBids, myLastRoundBid, currentRound]);

  // Which exit-bid rungs a category currently offers, given the quantity typed so far.
  // The rendered rows, the activity preview and the submitted payload all derive from this,
  // so the three cannot disagree about what is on offer.
  function exitBidEligibility(cat: any): { canSubmit: boolean; reduction: number; prevPrice: number; currPrice: number; maxBidPrice: number } {
    const reduction = (myLastRoundBid[cat.id] ?? 0) - (quantities[cat.id] ?? 0);
    const prevRound = [...(detail.rounds ?? [])].find((r: any) => r.roundNumber === currentRound.roundNumber - 1);
    const prevPrice = prevRound?.categoryPrices?.find((p: any) => p.categoryId === cat.id)?.price ?? 0;
    const currPrice = currentRound.categoryPrices.find((p: any) => p.categoryId === cat.id)?.price ?? 0;
    // The rule is prevPrice <= bid < currPrice. Prices are CHF thousands and 3.1.2 requires
    // multiples of CHF 1,000, so every bid is an integer and the largest valid one is
    // currPrice - 1. The bound must itself be an integer: stepping down from above the ceiling
    // clamps onto `max`, so a fractional bound would put a fraction in the field and every
    // later step down would carry it.
    return {
      canSubmit: currentRound.roundNumber > 1 && reduction > 0 && currPrice > prevPrice,
      maxBidPrice: currPrice - 1,
      reduction,
      prevPrice,
      currPrice,
    };
  }

  // Priced rungs that are actually on offer right now. Raising the clock quantity hides rungs
  // but deliberately keeps their typed prices in state, so they return intact if the quantity
  // goes back down -- which means a hidden rung still holds a price and must be filtered out
  // here. Submitting one would bid something the bidder cannot see.
  function visibleNewExitBids(): { categoryId: string; additionalQuantity: number; bidPrice: number }[] {
    const out: { categoryId: string; additionalQuantity: number; bidPrice: number }[] = [];
    for (const cat of detail.categories) {
      const { canSubmit, reduction } = exitBidEligibility(cat);
      if (!canSubmit) continue;
      for (const [addQtyStr, price] of Object.entries(newExitBidInputs[cat.id] ?? {})) {
        const addQty = parseInt(addQtyStr);
        if (price > 0 && addQty >= 1 && addQty <= reduction) {
          out.push({ categoryId: cat.id, additionalQuantity: addQty, bidPrice: price });
        }
      }
    }
    return out;
  }

  // Annex II 3.7.1-3.7.2, mirrored from services/auction_engine.py so the bidder can see
  // the quantity the server checks:
  //   clock activity + sum over categories of (points * LARGEST additional quantity
  //   among the exit bids submitted this round) <= eligibility + tolerance
  // Only the largest exit bid per category counts -- alternatives sharing a base are
  // mutually exclusive. Carried-forward rungs were charged in their own round and are
  // not counted again here.
  function activityBreakdown(): { clock: number; exit: number; total: number; limit: number; over: boolean } {
    if (!currentRound) {
      return { clock: 0, exit: 0, total: 0, limit: eligibility ?? 0, over: false };
    }

    const offered = visibleNewExitBids();
    let clock = 0;
    let exit = 0;
    for (const cat of detail.categories) {
      clock += (quantities[cat.id] ?? 0) * cat.activityPoints;
      const largest = offered
        .filter(eb => eb.categoryId === cat.id)
        .reduce((max, eb) => Math.max(max, eb.additionalQuantity), 0);
      exit += largest * cat.activityPoints;
    }

    const total = clock + exit;
    const limit = (eligibility ?? 0) + activityTolerance;
    return { clock, exit, total, limit, over: total > limit };
  }

  // What the bidder is about to send, for the confirmation dialog. Built from the same
  // values handleBid posts, so the dialog cannot show something other than what is sent.
  function bidConfirmationText(items: { categoryId: string; quantity: number }[]): string {
    const name = (id: string) => detail.categories.find((c: any) => c.id === id)?.name ?? id;
    const lines = [`Submit this bid for round ${currentRound.roundNumber}?`, '', 'Clock bid:'];
    for (const it of items) lines.push(`  ${name(it.categoryId)}: ${it.quantity}`);

    const exits = visibleNewExitBids();
    if (exits.length > 0) {
      lines.push('', 'New exit bids:');
      for (const eb of exits) {
        lines.push(`  ${name(eb.categoryId)}: +${eb.additionalQuantity} @ ${fmtPrice(eb.bidPrice)}`);
      }
    }

    // A withdrawn exit bid cannot be restored, so say so before it happens.
    if (activeExitBids.length > 0) {
      const withdrawn = activeExitBids.filter((eb: any) => !retainedExitBidIds.has(eb.id)).length;
      lines.push('', `Exit bids extended: ${activeExitBids.length - withdrawn}, withdrawn: ${withdrawn}`
        + (withdrawn > 0 ? ' (cannot be undone)' : ''));
    }

    const a = activityBreakdown();
    lines.push('', `Activity: ${a.total} of ${a.limit}`);
    return lines.join('\n');
  }

  async function handleBid(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const items = detail.categories.map((cat: any) => ({
        categoryId: cat.id,
        quantity: quantities[cat.id] ?? 0,
      }));
      if (!window.confirm(bidConfirmationText(items))) return;
      const res = await api.post(`/rounds/${currentRound.id}/bids`, {
        items,
        newExitBids: visibleNewExitBids(),
        retainedExitBidIds: Array.from(retainedExitBidIds),
      });
      setSubmitted(true);
      setMyBid({ items });
      setActiveExitBids(res.data.activeExitBids ?? []);
      setRetainedExitBidIds(new Set((res.data.activeExitBids ?? []).map((eb: any) => eb.id)));
    } catch (err: any) {
      const detailRaw = err.response?.data?.detail;
      // Structured rejections (ACTIVITY_LIMIT_EXCEEDED, CAP_EXCEEDED) carry both a short
      // `error` and a `message` that says what to change. Prefer the actionable one.
      const detail = typeof detailRaw === 'object'
        ? (detailRaw?.message ?? detailRaw?.error ?? JSON.stringify(detailRaw))
        : detailRaw;
      setError(detail ?? err.response?.data?.error ?? 'Failed to submit bid');
    }
  }

  const lastClosedRound = [...(detail.rounds ?? [])].reverse().find((r: any) => r.status === 'CLOSED');
  const roundStatus = getAuctionRoundStatus(detail);

  function triggerDownload(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadAggregateCsv() {
    const cats = detail.categories;
    const closedRounds = [...(detail.rounds ?? [])]
      .sort((a: any, b: any) => a.roundNumber - b.roundNumber)
      .filter((r: any) => r.status === 'CLOSED');
    const header = ['Round', ...cats.map((c: any) => `${c.name} Price`), ...cats.map((c: any) => `${c.name} Demand`)].join(',');
    const rows = closedRounds.map((r: any) => [
      r.roundNumber,
      ...cats.map((c: any) => r.categoryPrices?.find((x: any) => x.categoryId === c.id)?.price ?? ''),
      ...cats.map((c: any) => r.categoryPrices?.find((x: any) => x.categoryId === c.id)?.aggregateDemand ?? ''),
    ].join(','));
    triggerDownload([header, ...rows].join('\n'), `${detail.title.replace(/[^a-z0-9]/gi, '-')}-aggregate.csv`);
  }

  function downloadMyBidsCsv() {
    const cats = detail.categories;
    const closedRounds = [...(detail.rounds ?? [])]
      .sort((a: any, b: any) => a.roundNumber - b.roundNumber)
      .filter((r: any) => r.status === 'CLOSED');
    const header = ['Round', ...cats.map((c: any) => `${c.name} Price`), ...cats.map((c: any) => `${c.name} Bid`)].join(',');
    const rows = closedRounds.map((r: any) => [
      r.roundNumber,
      ...cats.map((c: any) => r.categoryPrices?.find((x: any) => x.categoryId === c.id)?.price ?? ''),
      ...cats.map((c: any) => myAllRoundBids[r.id]?.[c.id] ?? 0),
    ].join(','));
    triggerDownload([header, ...rows].join('\n'), `${detail.title.replace(/[^a-z0-9]/gi, '-')}-my-bids.csv`);
  }

  return (
    <div style={styles.page}>
      <button style={styles.backBtn} onClick={onBack}>← Back to Auctions</button>
      <div style={styles.card}>
        <div style={styles.header}>
          <h3 style={{ margin: 0 }}>{detail.title}</h3>
          <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', background: roundStatus.bg, color: roundStatus.color }}>{roundStatus.text}</span>
        </div>
        {detail.description && <p style={styles.meta}>{detail.description}</p>}

        <div style={styles.infoBox}>
          <button style={styles.collapseBtn} onClick={() => setShowCategories(h => !h)}>
            <span style={styles.chevron(showCategories)}>▶</span> Categories
          </button>
          {showCategories && (() => {
            const round1 = detail.rounds?.find((r: any) => r.roundNumber === 1);
            return (
              <table style={{ ...styles.table, marginTop: '0.75rem' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Category</th>
                    <th style={styles.thNum}>Activity Pts</th>
                    <th style={styles.thNum}>Supply</th>
                    <th style={styles.thNum}>Starting Price</th>
                    <th style={styles.thNum}>Price Increment %</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.categories.map((cat: any) => {
                    const p = round1?.categoryPrices?.find((x: any) => x.categoryId === cat.id);
                    return (
                      <tr key={cat.id}>
                        <td style={styles.td}>{cat.name}</td>
                        <td style={styles.tdNum}>{cat.activityPoints}</td>
                        <td style={styles.tdNum}>{cat.supply}</td>
                        <td style={styles.tdNum}>{fmtPrice(p?.price)}</td>
                        <td style={styles.tdNum}>{detail.priceIncrement}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>

        {/* Connection details for bidders who bid through the external API rather than
            this screen (the Excel/WinHttp tooling in docs/25). Everything here is already
            visible to this bidder elsewhere in the payload -- it is surfaced so the bidder
            can self-serve, instead of the auctioneer relaying ids out of band. */}
        <div style={styles.infoBox}>
          <button style={styles.collapseBtn} onClick={() => setShowIntegration(h => !h)}>
            <span style={styles.chevron(showIntegration)}>▶</span> Excel / API integration
          </button>
          {showIntegration && (() => {
            // The API base, NOT window.location.origin. On production they coincide
            // because nginx proxies /api on the same host, but in local development the
            // React dev server is on :3000 with no /api proxy while the backend is on
            // :4000 -- so origin would send the spreadsheet to the dev server, which
            // answers /api/... with the HTML page. Derive it from the same variable the
            // app itself uses, minus the trailing /api.
            const apiBase = (process.env.REACT_APP_API_URL ?? 'http://localhost:4000/api')
              .replace(/\/api\/?$/, '');
            const idTsv = detail.categories
              .map((c: any) => `${c.id}\t${c.name}`)
              .join('\n');
            const copy = async (what: string, text: string) => {
              try {
                await navigator.clipboard.writeText(text);
                setCopied(what);
                setTimeout(() => setCopied(''), 1500);
              } catch {
                // clipboard needs a secure context and can be blocked by policy; every
                // value below is also selectable by hand, so this is not a dead end
                setCopied('failed');
                setTimeout(() => setCopied(''), 2500);
              }
            };
            const field = (label: string, value: string, key: string) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                <span style={{ width: '7rem', color: '#555', fontSize: '0.8rem' }}>{label}</span>
                <input
                  readOnly
                  value={value}
                  onFocus={e => e.currentTarget.select()}
                  onClick={e => e.currentTarget.select()}
                  style={{ fontFamily: 'monospace', fontSize: '0.78rem', padding: '0.25rem 0.4rem', border: '1px solid #ddd', borderRadius: '4px', width: '23rem', maxWidth: '100%', background: 'white', color: '#333' }}
                />
                <button style={styles.copyBtn} onClick={() => copy(key, value)}>
                  {copied === key ? 'Copied' : 'Copy'}
                </button>
              </div>
            );
            return (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#666' }}>
                  Paste these into your spreadsheet's connection settings. You do not need
                  anything from the auctioneer.
                </p>
                {field('Base URL', apiBase, 'base')}
                {field('Auction ID', detail.id, 'auction')}
                <div style={{ marginTop: '0.9rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Categories</span>
                    <button style={styles.copyBtn} onClick={() => copy('cats', idTsv)}>
                      {copied === 'cats' ? 'Copied' : 'Copy ID + name (all rows)'}
                    </button>
                    <span style={{ fontSize: '0.75rem', color: '#888' }}>
                      pastes as two columns: category ID, then name
                    </span>
                  </div>
                  <div style={styles.scrollX}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Category ID</th>
                          <th style={styles.th}>Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.categories.map((cat: any) => (
                          <tr key={cat.id}>
                            <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.78rem' }}>{cat.id}</td>
                            <td style={styles.td}>{cat.name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {copied === 'failed' && (
                  <p style={{ color: '#c62828', fontSize: '0.78rem', marginTop: '0.6rem' }}>
                    Copy blocked by the browser — click a box and press Ctrl+C instead.
                  </p>
                )}
              </div>
            );
          })()}
        </div>

        {/* History */}
        {detail.rounds?.some((r: any) => r.status === 'CLOSED') && (
          <div style={styles.infoBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button style={styles.collapseBtn} onClick={() => setShowHistory(h => !h)}>
                <span style={styles.chevron(showHistory)}>▶</span> History
              </button>
              {showHistory && <button style={styles.csvBtn} onClick={downloadAggregateCsv}>Download Aggregate CSV</button>}
            </div>
            {showHistory && (() => {
              const sortedRounds = [...(detail.rounds ?? [])].sort((a: any, b: any) => a.roundNumber - b.roundNumber);
              const closedRounds = sortedRounds.filter((r: any) => r.status === 'CLOSED');
              return (
                <>
                  <h5 style={styles.subHeading}>Price &amp; Demand by Round</h5>
                  <div style={styles.scrollX}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.thNum}>Round</th>
                          {detail.categories.map((cat: any) => <th key={`${cat.id}-p`} style={styles.thNum}>{cat.name} Price</th>)}
                          {detail.categories.map((cat: any) => <th key={`${cat.id}-d`} style={styles.thNum}>{cat.name} Demand</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRounds.map((r: any) => (
                          <tr key={r.id}>
                            <td style={styles.tdNum}>{r.roundNumber}{r.status === 'ACTIVE' ? ' *' : ''}</td>
                            {detail.categories.map((cat: any) => {
                              const p = r.categoryPrices?.find((x: any) => x.categoryId === cat.id);
                              return <td key={`${cat.id}-p`} style={styles.tdNum}>{fmtPrice(p?.price)}</td>;
                            })}
                            {detail.categories.map((cat: any) => {
                              const p = r.categoryPrices?.find((x: any) => x.categoryId === cat.id);
                              return <td key={`${cat.id}-d`} style={styles.tdNum}>{r.status === 'CLOSED' ? (p?.aggregateDemand ?? '—') : '—'}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                    <h5 style={{ ...styles.subHeading, margin: 0 }}>My Bids by Round</h5>
                    <button style={styles.csvBtn} onClick={downloadMyBidsCsv}>Download My Bids CSV</button>
                  </div>
                  <div style={styles.scrollX}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.thNum}>Round</th>
                          {detail.categories.map((cat: any) => <th key={`${cat.id}-p`} style={styles.thNum}>{cat.name} Price</th>)}
                          {detail.categories.map((cat: any) => <th key={`${cat.id}-b`} style={styles.thNum}>{cat.name} Bid</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {closedRounds.map((r: any) => (
                          <tr key={r.id}>
                            <td style={styles.tdNum}>{r.roundNumber}</td>
                            {detail.categories.map((cat: any) => {
                              const p = r.categoryPrices?.find((x: any) => x.categoryId === cat.id);
                              return <td key={`${cat.id}-p`} style={styles.tdNum}>{fmtPrice(p?.price)}</td>;
                            })}
                            {detail.categories.map((cat: any) => (
                              <td key={`${cat.id}-b`} style={styles.tdNum}>{myAllRoundBids[r.id]?.[cat.id] ?? 0}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {currentRound && <p style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.5rem' }}>* Active round — demand not yet available</p>}
                </>
              );
            })()}
          </div>
        )}

        {/* Last round results */}
        {lastClosedRound && (
          <div style={styles.infoBox}>
            <button style={styles.collapseBtn} onClick={() => setShowRoundResults(h => !h)}>
              <span style={styles.chevron(showRoundResults)}>▶</span> Round {lastClosedRound.roundNumber} Results
            </button>
            {showRoundResults && <table style={{ ...styles.table, marginTop: '0.75rem' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Category</th>
                  <th style={styles.thNum}>Activity Pts</th>
                  <th style={styles.thNum}>Supply</th>
                  <th style={styles.thNum}>Price</th>
                  <th style={styles.thNum}>Aggregate Demand</th>
                  <th style={styles.thNum}>Excess Demand</th>
                  <th style={styles.thNum}>Bid</th>
                </tr>
              </thead>
              <tbody>
                {detail.categories.map((cat: any) => {
                  const p = lastClosedRound.categoryPrices.find((x: any) => x.categoryId === cat.id);
                  return (
                    <tr key={cat.id}>
                      <td style={styles.td}>{cat.name}</td>
                      <td style={styles.tdNum}>{cat.activityPoints}</td>
                      <td style={styles.tdNum}>{cat.supply}</td>
                      <td style={styles.tdNum}>{fmtPrice(p?.price)}</td>
                      <td style={styles.tdNum}>{p?.aggregateDemand ?? '—'}</td>
                      <td style={styles.tdNum}>{p?.aggregateDemand != null ? p.aggregateDemand - cat.supply : '—'}</td>
                      <td style={styles.tdNum}>{myLastRoundBid[cat.id] ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>}
          </div>
        )}

        {/* Active round — bid form */}
        {currentRound && (
          <div style={styles.roundBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>Round {currentRound.roundNumber} — ACTIVE</h4>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', color: timeLeft !== null && timeLeft <= 10 ? 'red' : '#333' }}>{timerLabel}</span>
            </div>
            {eligibility !== null && (() => {
              const a = activityBreakdown();
              return (
                <p data-testid="activity-strip" style={styles.eligibility}>
                  Your eligibility: <strong>{eligibility}</strong>
                  {activityTolerance > 0 && (
                    <span style={{ color: '#666' }}> (+{activityTolerance} allowance)</span>
                  )}
                  &nbsp;|&nbsp;
                  Clock: <strong data-testid="activity-clock">{a.clock}</strong>
                  &nbsp;|&nbsp;
                  Exit bids: <strong data-testid="activity-exit">{a.exit}</strong>
                  &nbsp;|&nbsp;
                  Total activity:{' '}
                  <strong data-testid="activity-total" style={{ color: a.over ? '#c62828' : 'inherit' }}>
                    {a.total}
                  </strong>
                  /{a.limit}{a.over ? ' \u26a0' : ''}
                </p>
              );
            })()}
            {(detail.caps ?? []).length > 0 && (
              <div style={{ fontSize: '0.875rem', margin: '0.25rem 0 0.5rem', padding: '0.4rem 0.75rem', background: '#f9f9f9', borderRadius: '4px', border: '1px solid #eee', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                <strong style={{ color: '#555' }}>Caps:</strong>
                {(detail.caps ?? []).map((cap: any) => {
                  const usage = (cap.terms ?? []).reduce((sum: number, t: any) => sum + (quantities[t.categoryId] ?? 0) * t.coefficient, 0);
                  const over = usage > cap.limit;
                  return (
                    <span key={cap.id} style={{ color: over ? '#c62828' : '#333' }}>
                      {cap.name}: <strong style={{ color: over ? '#c62828' : 'inherit' }}>{usage}</strong>/{cap.limit}{over ? ' ⚠' : ''}
                    </span>
                  );
                })}
              </div>
            )}
            <form onSubmit={handleBid} style={styles.form}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Category</th>
                    <th style={styles.thNum}>Activity Pts</th>
                    <th style={styles.thNum}>Supply</th>
                    <th style={styles.thNum}>Price</th>
                    <th style={styles.thNum}>Bid</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.categories.map((cat: any) => {
                    const price = currentRound.categoryPrices.find((p: any) => p.categoryId === cat.id);
                    const currQty = quantities[cat.id] ?? 0;
                    const { canSubmit: canSubmitExitBid, reduction, prevPrice, currPrice, maxBidPrice } = exitBidEligibility(cat);
                    return (
                      <React.Fragment key={cat.id}>
                        <tr>
                          <td style={styles.td}>{cat.name}</td>
                          <td style={styles.tdNum}>{cat.activityPoints}</td>
                          <td style={styles.tdNum}>{cat.supply}</td>
                          <td style={styles.tdNum}>{fmtPrice(price?.price)}</td>
                          <td style={styles.tdNum}>
                            <input data-testid={`qty-${cat.name}`} type="number" min="0" max={cat.supply} style={styles.qtyInput}
                              value={quantities[cat.id] ?? 0}
                              onChange={e => setQuantities(q => ({ ...q, [cat.id]: parseInt(e.target.value) || 0 }))} />
                          </td>
                        </tr>
                        {canSubmitExitBid && Array.from({ length: reduction }, (_, i) => i + 1).map(n => (
                          <tr key={`eb-${cat.id}-${n}`} style={{ background: '#f3f0ff' }}>
                            <td colSpan={5} style={{ padding: '0.25rem 0.6rem 0.25rem 1.4rem', borderBottom: '1px solid #ede8ff' }}>
                              <div style={styles.ebRow}>
                                <span style={{ ...styles.ebLabel, color: '#6a1b9a' }}>New</span>
                                <span style={styles.ebQty}>{currQty}+{n}@</span>
                                <input
                                  type="number" min={prevPrice} max={maxBidPrice} step="1"
                                  placeholder="price"
                                  style={styles.ebPriceInput}
                                  value={(newExitBidInputs[cat.id]?.[n]) || ''}
                                  onChange={e => {
                                    const price = parseFloat(e.target.value) || 0;
                                    setNewExitBidInputs(prev => ({
                                      ...prev,
                                      [cat.id]: { ...(prev[cat.id] ?? {}), [n]: price },
                                    }));
                                  }}
                                />
                                <span style={styles.ebHint}>({fmtPrice(prevPrice)} ≤ p &lt; {fmtPrice(currPrice)})</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {activeExitBids.filter((eb: any) => eb.categoryId === cat.id).map((eb: any) => {
                          const isRetained = retainedExitBidIds.has(eb.id);
                          return (
                            <tr key={`ret-${eb.id}`} style={{ background: '#fff8e1' }}>
                              <td colSpan={5} style={{ padding: '0.25rem 0.6rem 0.25rem 1.4rem', borderBottom: '1px solid #ffe082' }}>
                                <label style={{ ...styles.ebRow, cursor: 'pointer' }}>
                                  <span style={styles.ebLabel}>
                                    <input type="checkbox" checked={isRetained}
                                      onChange={e => setRetainedExitBidIds(prev => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(eb.id); else next.delete(eb.id);
                                        return next;
                                      })} />
                                  </span>
                                  <span style={styles.ebQty}>{eb.fromQuantity}+{eb.incrementQuantity}@</span>
                                  <span style={styles.ebPriceStatic}>{fmtPrice(eb.bidPrice)}</span>
                                  <span style={styles.ebHint}>from round {eb.sourceRoundNumber}</span>
                                </label>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {error && <p style={styles.error}>{error}</p>}
              {submitted && <p style={styles.success}>Bid submitted. You can update it before the round closes.</p>}
              <button type="submit" style={styles.bidBtn}>
                {submitted ? 'Update Bid' : 'Submit Bid'}
              </button>
            </form>
          </div>
        )}

        {detail.status === 'PENDING' && <p style={styles.meta}>Waiting for auctioneer to open the first round.</p>}
        {detail.status === 'CLOCK_COMPLETE' && (
          <div style={{ ...styles.roundBox, borderColor: '#f57f17', background: '#fff8e1' }}>
            <h4 style={{ color: '#f57f17' }}>Clock Rounds Complete</h4>
            <p>All clock rounds have closed. Results are pending — the auctioneer will process the final allocation.</p>
          </div>
        )}
        {detail.status === 'CLOSED' && (
          <div style={{ ...styles.roundBox, borderColor: '#1e7e34', background: '#e6f4ea' }}>
            <h4 style={{ color: '#1e7e34', marginTop: 0 }}>
              Auction Closed — Final Results
              {finalResults && ` | Round ${finalResults.finalRound} | Total Revenue: ${finalResults.totalRevenue.toLocaleString()}`}
            </h4>
            {!finalResults ? (
              <p style={styles.meta}>Loading results…</p>
            ) : (
              <>
                <ResultsByBidder results={finalResults} />
                <h5 style={{ ...styles.subHeading, color: '#1e7e34', fontSize: '1rem' }}>Results by Band</h5>
                {finalResults.categories.map((cat: any) => {
                  const sortedAllocs = [...cat.allocations].sort((a: any, b: any) =>
                    a.bidderName.localeCompare(b.bidderName, undefined, { numeric: true })
                  );
                  const totalAllocated = cat.totalClockAllocated + cat.totalExtensionAllocated;
                  const unallocated = cat.supply - totalAllocated;
                  const catTotalPayment = sortedAllocs.reduce((s: number, a: any) => s + a.totalPayment, 0);
                  return (
                    <div key={cat.categoryId} style={{ marginBottom: '1.25rem' }}>
                      <h5 style={{ ...styles.subHeading, marginBottom: '0.4rem' }}>
                        {cat.categoryName} — Supply: {cat.supply} | Clock Price: {cat.clockPrice.toLocaleString()} | Residual: {cat.residualSupply}
                      </h5>
                      <div style={styles.scrollX}>
                        <table style={styles.table}>
                          <thead>
                            <tr>
                              <th style={styles.th}>Bidder</th>
                              <th style={styles.thNum}>Clock Qty</th>
                              <th style={styles.thNum}>Ext Qty</th>
                              <th style={styles.thNum}>Total Qty</th>
                              <th style={styles.thNum}>Clock Payment</th>
                              <th style={styles.thNum}>Ext Revenue</th>
                              <th style={styles.thNum}>Total Payment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedAllocs.map((a: any) => (
                              <tr key={a.bidderId}>
                                <td style={styles.td}>{a.bidderName}</td>
                                <td style={styles.tdNum}>{a.clockQuantity}</td>
                                <td style={styles.tdNum}>{a.extensionQuantity}</td>
                                <td style={styles.tdNum}>{a.totalQuantity}</td>
                                <td style={styles.tdNum}>{a.clockPayment.toLocaleString()}</td>
                                <td style={styles.tdNum}>{a.extensionRevenue.toLocaleString()}</td>
                                <td style={styles.tdNum}>{a.totalPayment.toLocaleString()}</td>
                              </tr>
                            ))}
                            <tr style={{ fontWeight: 'bold', borderTop: '2px solid #ccc' }}>
                              <td style={styles.td}><strong>Total Allocated</strong></td>
                              <td style={styles.tdNum}><strong>{cat.totalClockAllocated}</strong></td>
                              <td style={styles.tdNum}><strong>{cat.totalExtensionAllocated}</strong></td>
                              <td style={styles.tdNum}><strong>{totalAllocated}</strong></td>
                              <td style={styles.tdNum}><strong>{(cat.totalClockAllocated * cat.clockPrice).toLocaleString()}</strong></td>
                              <td style={styles.tdNum}></td>
                              <td style={styles.tdNum}><strong>{catTotalPayment.toLocaleString()}</strong></td>
                            </tr>
                            <tr style={{ background: '#f1f8e9' }}>
                              <td style={styles.td}><strong>Unallocated</strong></td>
                              <td style={styles.tdNum}></td>
                              <td style={styles.tdNum}></td>
                              <td style={{ ...styles.tdNum, color: unallocated > 0 ? '#e65100' : 'inherit' }}>
                                <strong>{unallocated}</strong>
                              </td>
                              <td style={styles.tdNum}>—</td>
                              <td style={styles.tdNum}>—</td>
                              <td style={styles.tdNum}>—</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
                <div style={{ borderTop: '2px solid #ccc', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
                  <strong>Grand Total Revenue: {finalResults.totalRevenue.toLocaleString()}</strong>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: any = {
  page: { padding: '2rem' },
  card: { background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  badge: (status: string) => ({
    display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold',
    background: status === 'ACTIVE' ? '#e6f4ea' : status === 'CLOSED' ? '#fce8e6' : '#e8eaf6',
    color: status === 'ACTIVE' ? '#1e7e34' : status === 'CLOSED' ? '#c62828' : '#3949ab',
  }),
  meta: { color: '#666', fontSize: '0.875rem' },
  eligibility: { fontSize: '0.9rem', margin: '0.5rem 0' },
  infoBox: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '1rem', margin: '1rem 0', background: '#fafafa' },
  collapseBtn: { background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '1rem', padding: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' },
  csvBtn: { padding: '0.4rem 0.8rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' },
  chevron: (open: boolean) => ({ display: 'inline-block', transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: '0.75rem' }),
  subHeading: { margin: '0.75rem 0 0.5rem', fontWeight: 600, color: '#444', fontSize: '0.9rem' },
  scrollX: { overflowX: 'auto' as const },
  copyBtn: { padding: '0.25rem 0.6rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' },
  roundBox: { border: '1px solid #4a90e2', borderRadius: '6px', padding: '1rem', marginTop: '1rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { padding: '0.4rem 0.6rem', borderBottom: '2px solid #ddd', textAlign: 'left' as const, fontWeight: 600, color: '#555' },
  thNum: { padding: '0.4rem 0.6rem', borderBottom: '2px solid #ddd', textAlign: 'right' as const, fontWeight: 600, color: '#555' },
  td: { padding: '0.35rem 0.6rem', borderBottom: '1px solid #f0f0f0' },
  tdNum: { padding: '0.35rem 0.6rem', borderBottom: '1px solid #f0f0f0', textAlign: 'right' as const },
  qtyInput: { width: '70px', padding: '0.3rem', border: '1px solid #ddd', borderRadius: '4px', textAlign: 'center' },
  bidBtn: { padding: '0.6rem 1.5rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-start' },
  backBtn: { marginBottom: '1rem', padding: '0.4rem 0.8rem', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' },
  error: { color: 'red', fontSize: '0.875rem', margin: 0 },
  success: { color: '#1e7e34', fontSize: '0.875rem', margin: 0 },
  exitBidSection: { background: '#f3f0ff', border: '1px solid #d0c4ff', borderRadius: '6px', padding: '0.75rem 1rem', marginTop: '0.5rem' },
  exitBidRow: { display: 'flex', flexWrap: 'wrap' as const, gap: '0.4rem', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid #e0d8ff' },
  exitInput: { width: '80px', padding: '0.3rem', border: '1px solid #c4b8ff', borderRadius: '4px', fontSize: '0.87rem' },
  ebRow: { display: 'flex', alignItems: 'center', gap: '0', fontSize: '0.83rem' },
  ebLabel: { width: '46px', flexShrink: 0, fontWeight: 600, fontSize: '0.83rem' },
  ebQty: { width: '72px', flexShrink: 0, color: '#555' },
  ebPriceInput: { width: '110px', flexShrink: 0, padding: '0.25rem 0.4rem', border: '1px solid #c4b8ff', borderRadius: '4px', fontSize: '0.83rem' },
  ebPriceStatic: { width: '110px', flexShrink: 0, color: '#333', paddingLeft: '0.4rem' },
  ebHint: { color: '#aaa', fontSize: '0.78rem', paddingLeft: '0.5rem' },
};
